import { computed, ref, watch } from '@nuxtjs/composition-api';
import useCustomer from '@shared/composables/useCustomer';
import { getCookie, setCookie } from '@shared/helpers/cookies';
import { useUser } from '@unified-commerce/gpc-vue-storefront-shopify';
import { Logger } from '@vue-storefront/core';

import { getHashedUserId } from './helpers/getHashedUserId';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

const COOKIE_NAME = 'customUserId';

const useCustomUserId = () => {
  if (!process.client) {
    return { customUserId: ref<string | null>(null) };
  }

  const domain = window.location.hostname.replace('www', '');
  const customUserId = ref<string | null>(null);
  const { user } = useUser();
  const { customer } = useCustomer();

  const userId = computed(() => getHashedUserId({ user: user.value, customer: customer.value }));

  const getCustomUserId = (): string | null => {
    try {
      const cookieId = getCookie(COOKIE_NAME);

      if (cookieId) {
        if (localStorage.getItem(COOKIE_NAME) !== cookieId) {
          localStorage.setItem(COOKIE_NAME, cookieId);
        }
        return cookieId;
      }

      const lsId = localStorage.getItem(COOKIE_NAME);

      if (lsId) {
        setCookie(COOKIE_NAME, lsId, domain);
        return lsId;
      }

      const newId = userId.value;

      setCookie(COOKIE_NAME, newId, domain);
      localStorage.setItem(COOKIE_NAME, newId);
      return newId;
    } catch (e) {
      Logger.warn('Error getting customUserId');
      return null;
    }
  };

  watch([user, customer], () => {
    if (userId.value) {
      customUserId.value = getCustomUserId();
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        algoliaAuthenticatedUserToken: customUserId.value,
      });
    }
  });

  return { customUserId };
};

export default useCustomUserId;
